/**
 * Subscriber and onboarding schemas. PRD-003 DATA-01, DATA-18.
 *
 * Note: subscribers.tier and subscribers.status are mutated only by the Stripe
 * webhook handler per ARCH-001 §7. Application code may read these fields but
 * never write them outside that handler.
 */

import { z } from 'zod';
import { emailSchema, isoTimestampSchema, uuidSchema } from './common.js';
import {
  emailFrequencySchema,
  onboardingStatusSchema,
  subscriberStatusSchema,
  tierSchema,
} from './enums.js';

export const subscriberSchema = z.object({
  id: uuidSchema,
  user_id: uuidSchema, // FK to auth.users
  email: emailSchema,
  tier: tierSchema,
  status: subscriberStatusSchema,
  stripe_customer_id: z.string().min(1),
  stripe_subscription_id: z.string().min(1).nullable(),
  email_frequency: emailFrequencySchema.default('immediate'),
  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
  paused_at: isoTimestampSchema.nullable(),
  paused_until: isoTimestampSchema.nullable(),
  canceled_at: isoTimestampSchema.nullable(),
  deleted_at: isoTimestampSchema.nullable(),
});
export type Subscriber = z.infer<typeof subscriberSchema>;

/**
 * Onboarding state. PRD-003 DATA-18 + PR-07.
 *
 * welcome_view_block and welcome_email_body are composed by Eric in the
 * operator review queue (T-035). Both are persisted before the welcome view
 * fires on the subscriber's next portal entry.
 */
export const onboardingStateSchema = z.object({
  id: uuidSchema,
  subscriber_id: uuidSchema,
  onboarding_status: onboardingStatusSchema,
  onboarding_token: z.string().min(16),
  submitted_at: isoTimestampSchema.nullable(),
  eric_reviewed_at: isoTimestampSchema.nullable(),
  welcome_email_body: z.string().nullable(),
  welcome_view_block: z.string().nullable(),
  welcome_view_dismissed_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
});
export type OnboardingState = z.infer<typeof onboardingStateSchema>;
